import React, {
  useCallback,
  useContext,
  useMemo,
  useState,
  useEffect,
  useReducer,
} from "react";
import PropTypes from "prop-types";
import LazyComponent from "venuescanner-js/react/components/LazyComponent";
import StageContainer from "venuescanner-js/react/components/FormStageContainer";

let pathname;
const useRewindHistoryOnMount = (shouldUse) =>
  useEffect(() => {
    if (!shouldUse) return;
    if (typeof window !== "undefined") {
      pathname = window.location.pathname;
      const { state } = window.history;

      if (typeof state?.stageIdx === "number") {
        window.history.go(-state.stageIdx);
        window.history.pushState(
          { stage: undefined, stageIdx: undefined },
          "",
          pathname
        );
      }
    }
  }, [shouldUse]);

const useHistory = ({ stageIds, setCurrentStageId }, shouldUse) => {
  useRewindHistoryOnMount(shouldUse);
  const listener = useCallback(
    ({ state }) => {
      if (!shouldUse || window.skipPopstate) return;
      const newStage = state?.stage || stageIds[0];
      setCurrentStageId(newStage);
    },
    [stageIds, setCurrentStageId, shouldUse]
  );

  useEffect(() => {
    if (!shouldUse) return;
    window.onpopstate = listener;

    return () => {
      delete window.onpopstate;
    };
  }, [shouldUse, listener]);
};

export const StageContext = React.createContext({
  shouldUseHistory: false,
  isLoading: false,
  isLast: false,
  isFirst: true,
  setIsLoading() {},
  stageRepo: [],
  numberOfStages: 0,
  setStageRepo() {},
  currentStageId: undefined,
  currentStageIdx: 0,
  setCurrentStageId() {},
  nextStage() {},
  prevStage() {},
});

export const LazyStageContainer = () => {
  const { stageRepo, currentStageId } = useContext(StageContext);
  const stageEntries = useMemo(
    () => Array.from(stageRepo.entries()),
    [stageRepo]
  );
  const currentStageIdx = useMemo(
    () => stageEntries.findIndex(([stageId]) => currentStageId === stageId),
    [stageEntries, currentStageId]
  );
  const components = useMemo(() => {
    const loadedStages = stageEntries.filter(
      ([, component]) => !(component instanceof LazyComponent)
    );
    return Array.from(new Map(loadedStages).entries()).map(
      ([stageId, Comp]) => <Comp key={stageId} />
    );
  }, [stageEntries]);

  return <StageContainer stage={currentStageIdx}>{components}</StageContainer>;
};

export const StageContextProvider = ({
  stages,
  useHistory: shouldUseHistory,
  children,
}) => {
  const [[firstStage]] = stages.entries();
  const [isLoading, setIsLoading] = useState(false);
  const [stageRepo, setStageRepo] = useReducer(
    (prevStageRepo, nextStageRepo) => {
      return new Map(
        Array.from(nextStageRepo.entries()).map(([stageId, Component]) => {
          const prevStage = prevStageRepo.get(stageId);
          return [
            stageId,
            !prevStage || prevStage instanceof LazyComponent
              ? Component
              : prevStage,
          ];
        })
      );
    },
    stages
  );
  const [currentStageId, setCurrentStageId] = useState(firstStage);
  const stageIds = useMemo(() => Array.from(stageRepo.keys()), [stageRepo]);
  const isFirst = currentStageId === stageIds[0];
  const isLast = currentStageId === stageIds[stageIds.length - 1];
  const currentStageIdx = stageIds.indexOf(currentStageId);
  const nextStageId = stageIds[currentStageIdx + 1];

  useHistory({ stageIds, setCurrentStageId }, shouldUseHistory);

  const navigateToStage = useCallback(
    (stageId, navigate = true) => {
      const stageIdx = stageIds.indexOf(stageId);
      setCurrentStageId(stageId);
      if (navigate && shouldUseHistory)
        window.history.pushState({ stage: stageId, stageIdx }, "", pathname);
    },
    [setCurrentStageId, shouldUseHistory, stageIds]
  );

  const nextStage = useCallback(() => {
    if (!nextStageId) return;
    const nextStage = stageRepo.get(nextStageId);
    (async () => {
      if (nextStage instanceof LazyComponent) {
        setIsLoading(true);
        // Wait 150ms to let the button change to loading state before blocking the UI
        const loadedStage = await new Promise((resolve) =>
          setTimeout(() => nextStage.load().then(resolve), 150)
        );
        const nextStageRepo = new Map(stageRepo);
        nextStageRepo.set(nextStageId, loadedStage);
        setStageRepo(nextStageRepo);
        setIsLoading(false);
      }

      navigateToStage(nextStageId);
    })();
  }, [navigateToStage, setIsLoading, stageRepo, nextStageId]);

  const prevStage = useCallback(() => {
    const prevStageId = stageIds[currentStageIdx - 1];
    if (prevStageId) navigateToStage(prevStageId);
  }, [navigateToStage, currentStageIdx, stageIds]);

  const numberOfStages = stageIds.length;

  const providerValue = useMemo(
    () => ({
      isLoading,
      setIsLoading,
      stageRepo,
      numberOfStages,
      setStageRepo,
      currentStageId,
      currentStageIdx,
      setCurrentStageId,
      nextStage,
      prevStage,
      isFirst,
      isLast,
      nextStageId,
    }),
    [
      isLoading,
      setIsLoading,
      stageRepo,
      numberOfStages,
      setStageRepo,
      currentStageId,
      currentStageIdx,
      setCurrentStageId,
      nextStage,
      prevStage,
      isFirst,
      isLast,
      nextStageId,
    ]
  );

  return (
    <StageContext.Provider value={providerValue}>
      {children}
    </StageContext.Provider>
  );
};

StageContextProvider.propTypes = {
  stages: PropTypes.instanceOf(Map),
  useHistory: PropTypes.bool,
  children: PropTypes.node,
};
